import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  NgClass,
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgTemplateOutlet,
} from '@angular/common';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  skip,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';

import { ComparisonService } from '../../../services/comparison.service';
import { LayersMenuService } from '../../../../menu/right-menu/layers-menu/layers-menu.service';
import { UserAccessService } from '../../../../../user/access/user-access.service';
import { formatLayerName } from '../../../../common-colorscale/layer-name-format.pipe';
import { formatNumbers } from '../../../../../shared/pipes/number-formatter.pipe';
import { SubscriptionPlans } from '../../../../../user/user/user.model';
import {
  COUNTY_LEVEL_LAYER,
  H3_RES5_LEVEL_LAYER,
  H3_RES7_LEVEL_LAYER,
} from '../../../services/layer-store.service';
import {
  COMBINED_HWW_DESCRIPTION,
  CUMULATIVE_TRAFFIC_INTENSITY,
  LAND_AREA_FRACTION,
  MAP_AI_SOURCE_NOTE,
  POPULATION,
} from '../../../../../shared/types/feature-data-type';
import {
  COUNTY_ONLY_FEATURES,
  COUNTY_ONLY_UNAVAILABLE_NOTE,
  CUSTOMERS_SECTION_ID,
  FREE_GENERATION_ID,
  GLANCE_LABELS,
  GLANCE_TILES,
  HERO_SCORE,
  HERO_SUB_INDICES,
  OVERVIEW_TAB_ID,
  POPUP_TABS,
  PopupIcon,
  PopupTab,
  TRAFFIC_SECTION_ID,
  TRAFFIC_TEASER_SECTION_ID,
} from '../popup-groups';
import {
  GenerationSlice,
  generationBreakdown,
} from '../../../../../customer-profile/generations';
import { mapAgeSexDistribution } from '../../../../../shared/model/population-mapper';
import {
  hasNationalComparison,
  MetricDeviation,
  nationalDeviation,
  nationalValueOf,
} from '../national-comparison';
import { splitUnit } from '../split-unit';
import {
  formatTrafficCount,
  summariseTraffic,
  TrafficSummary,
} from '../traffic-summary';
import { MapHttpService } from '../../../services/map-http.service';
import { SelectedCellService } from '../../../services/selected-cell.service';
import { hwwGrade, HwwGrade } from '../hww-grade';
import {
  ACCOUNT_LOCK,
  LockCopy,
  lockCopyFor,
} from '../../../../menu/right-menu/layers-menu/shared/feature-tooltip/inacessible-features-util';
import { FeatureTooltipComponent } from '../../../../menu/right-menu/layers-menu/shared/feature-tooltip/feature-tooltip.component';
import { MapModalData } from '../../../../../user/map-redirect-modal/map-redirect-modal.component';
import { ModalService } from '../../../../../shared/services/modal.service';
import { UserGuideService } from '../../../../user-guide/user-guide.service';
import { LockIconComponent } from '../../../../../shared/components/lock-icon/lock-icon.component';
import { TrafficTeaserComponent } from '../traffic-teaser/traffic-teaser.component';

export interface MetricRow {
  feature: string;
  label: string;
  value: string;
  /** Null when the visitor may see the number. */
  lock: LockCopy | null;
  deviation: MetricDeviation | null;
  /** `US avg 78,538` — the reference the bar is drawn against. */
  reference: string;
  /** Long-form comparison, shown on hover and read by screen readers. */
  note: string;
  /**
   * The metric is one the map can be painted with, so the row is a control:
   * clicking it recolours the map the way the right menu's radio would. False
   * for a locked row (the padlock owns that click) and for anything the menu
   * has no map layer for.
   */
  colorable: boolean;
  /** Already the layer on the map — the row says so instead of offering it. */
  onMap: boolean;
  /**
   * Set only on the HWW indices, which are graded rather than compared: the
   * row draws the score block's meter in place of the US bar. Null everywhere
   * else, and on a locked row.
   */
  grade: HwwGrade | null;
  /** No value yet and its request is still out - see `pendingFeatures`. */
  pending: boolean;
}

export interface GlanceTile extends MetricRow {
  icon: PopupIcon;
  caption: string;
}

export interface SubIndexRow {
  label: string;
  grade: HwwGrade | null;
  locked: boolean;
}

export interface RenderSection {
  /** See PopupSection.id. */
  id: string | null;
  label: string | null;
  note: string | null;
  rows: MetricRow[];
  /** See PopupSection.alwaysShown. */
  alwaysShown: boolean;
}

export interface RenderTab {
  id: string;
  label: string;
  icon: PopupIcon;
  sections: RenderSection[];
  /**
   * The padlock on the tab itself, or null when nothing inside is hidden.
   * A tab holding even one paywalled row wears the paid padlock: it is the
   * stronger claim, and signing up would not empty the tab.
   */
  lock: LockCopy | null;
}

/** A lazily loaded section card. `empty`: no data for the cell; `failed`: request error. */
export type CardState = 'idle' | 'loading' | 'ready' | 'empty' | 'failed';

/** Short names for the hero meters. The full names live in the tooltips. */
const SUB_INDEX_LABELS = ['Health', 'Income', 'Education'];

/** How many metrics the overview calls out as unusual for this place. */
const STANDOUT_COUNT = 4;

/** Subpixel scroll heights, so the last pixel of a panel is never reachable. */
const EDGE_TOLERANCE = 2;

/**
 * Wheel distance past the end of a panel that carries over into the next tab.
 * A mouse notch is ~100px and clears it on its own; a trackpad accumulates.
 */
const TAB_SCROLL_THRESHOLD = 90;

/** Same, for a finger: a deliberate drag rather than the tail of a flick. */
const TAB_SWIPE_THRESHOLD = 60;

/**
 * How long the panel has to sit at its edge before the overscroll starts
 * counting. Without it the momentum of the flick that reached the bottom is
 * read as the gesture asking for the next tab, and one flick crosses two.
 */
const EDGE_DWELL_MS = 140;

/** After a switch, the tail of the same gesture must not carry into a third tab. */
const TAB_SWITCH_COOLDOWN_MS = 450;

/** A line of wheel delta in `DOM_DELTA_LINE` mode, which is what Firefox sends. */
const WHEEL_LINE_HEIGHT = 16;

/**
 * How long the outgoing panel has to clear out before the incoming one is
 * rendered. The strip highlights the new tab at once, so the gesture is
 * answered immediately and only the list waits - together with the entry
 * animation this stays well inside TAB_SWITCH_COOLDOWN_MS.
 */
const PANEL_LEAVE_MS = 130;

/**
 * The body of the location popup: a national grade, then four subject tabs.
 *
 * Replaced the flat list of every property this popup used to show. Four
 * things changed and each is load-bearing:
 *
 *  - Every row is drawn against the national value the ComparisonService
 *    already caches, so the comparison stops being a sentence at the end of a
 *    line and becomes the row's shape.
 *  - Subjects are tabs rather than one long scroll, so the card opens at a
 *    readable height on a phone and each panel is a short, complete list.
 *  - The overview names the handful of metrics furthest from the US average,
 *    which is the analysis a reader would otherwise have to do by scrolling.
 *  - Features the visitor's plan does not cover are *rendered locked* rather
 *    than filtered out. Hiding them meant the strongest upgrade argument this
 *    product has — the data sitting one click away — was invisible.
 */
@Component({
  selector: 'app-grouped-popup-list',
  templateUrl: './grouped-popup-list.component.html',
  styleUrls: ['./grouped-popup-list.component.scss'],
  imports: [
    NgIf,
    NgFor,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    NgTemplateOutlet,
    FeatureTooltipComponent,
    LockIconComponent,
    TrafficTeaserComponent,
  ],
})
export class GroupedPopupListComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  private readonly guideService = inject(UserGuideService);
  private readonly zone = inject(NgZone);

  @ViewChild('panel') private panelRef?: ElementRef<HTMLElement>;

  /**
   * The metric the getting-started guide is pointing at, mirrored from the
   * service into a field so the rows can test it without an async pipe each.
   * The guide highlights it here as well as in the data panel, so a visitor
   * who opens a cell before ever reaching the right menu still meets it.
   */
  public guidedFeature: string | null = null;
  public guidedPulse = false;

  @Input() properties!: { [key: string]: any };
  @Input() title!: string;
  /** What kind of area the title names - see MapPopupComponent.areaKind. */
  @Input() subtitle = '';
  @Input() activeLayer!: string;
  @Input() activeLevel!: string;
  /** Drawn instead of `N/A` while the colourscale for `activeLayer` loads. */
  @Input() isLoading = false;

  /** The score block's own request is still out - see the shell's loadAreaScore. */
  @Input() isScoreLoading = false;

  /**
   * Upper-cased metrics still being fetched. An empty one is drawn as a
   * placeholder row rather than dropped, which keeps its section and tab on
   * screen: otherwise the strip grew tab by tab as the requests answered.
   */
  @Input() pendingFeatures: ReadonlySet<string> = new Set();

  /** The tab to open on, read once - the shell remembers it across cells. */
  @Input() initialTabId = OVERVIEW_TAB_ID;

  @Output() tabSelected = new EventEmitter<string>();

  /**
   * A locked metric the visitor asked to see. The shell owns the upsell modal
   * and the analytics that go with it: this component only knows what was
   * clicked, which keeps it free of the modal/GA dependency graph.
   *
   * `feature` is omitted for blocks with no map indicator behind them, which
   * falls back to the free sign-up prompt.
   */
  @Output() unlockRequested = new EventEmitter<{
    feature?: string;
    label: string;
  }>();

  /**
   * A readable metric was clicked and should become the map's colour. The
   * shell owns the LayerStore write for the same reason it owns the upsell
   * modal: this component reports what was clicked and nothing else.
   */
  @Output() colorByRequested = new EventEmitter<{
    feature: string;
    label: string;
  }>();

  public heroTitle = '';
  public heroGrade: HwwGrade | null = null;
  public heroLock: LockCopy | null = null;
  public subIndices: SubIndexRow[] = [];

  /**
   * The (?) beside the title: the right menu's own hint for this indicator,
   * plus the one thing that is only true in the popup - the card always grades
   * against the country, while the map can be painted with either scope.
   */
  public readonly scoreDescription =
    `${COMBINED_HWW_DESCRIPTION} On this card it is always graded against the whole US; ` +
    `the data menu can repaint the map with the state comparison instead. ` +
    MAP_AI_SOURCE_NOTE;

  public activeRow: MetricRow | null = null;

  public trafficState: CardState = 'idle';
  public trafficOverall = '';
  public trafficSplit = '';
  public trafficLock: LockCopy | null = null;

  public generations: GenerationSlice[] = [];
  public generationsState: CardState = 'idle';
  /** Census year the cohort ages are read against. */
  public generationsYear = 0;
  /** Account gate on every cohort but the free one; never a plan gate. */
  public generationsLock: LockCopy | null = null;

  public glance: GlanceTile[] = [];
  public standouts: MetricRow[] = [];
  public tabs: RenderTab[] = [];

  /** What the strip highlights: set the instant the gesture asks for it. */
  public activeTabId = OVERVIEW_TAB_ID;

  /**
   * What the panel draws. It trails `activeTabId` by the length of the leaving
   * animation, which is what lets the outgoing list clear out before the
   * incoming one arrives rather than the two cutting from one to the other.
   */
  public renderedTabId = OVERVIEW_TAB_ID;

  /** Wheel distance accumulated past the edge of the panel, in CSS pixels. */
  private overscroll = 0;
  private edgeSince = 0;
  private lastSwitchAt = 0;
  private swapTimer: number | null = null;
  private touchStartY: number | null = null;
  private touchFromStart = false;
  private touchFromEnd = false;
  private detachScrollChaining: (() => void) | null = null;

  private comparisonNotes: { [feature: string]: string } = {};
  private lookup = new Map<string, any>();
  private mapColorCache: Set<string> | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly properties$ = new Subject<{ [key: string]: any }>();

  constructor(
    private comparisonService: ComparisonService,
    private accessService: UserAccessService,
    private layersMenuService: LayersMenuService,
    private modalService: ModalService,
    private http: MapHttpService,
    private selectedCellService: SelectedCellService
  ) {
    // `switchMap`, because the shell fills `properties` in more than once per
    // popup (MapPopupComponent's written-in-state and area-score loads both
    // mutate it). Re-subscribing per change left every one of those requests in
    // flight, each having read the same empty localStorage and each issuing the
    // same fetch; only the last answer is wanted, and cancelling the rest is
    // what stops the duplicates.
    this.properties$
      .pipe(
        switchMap((properties) => this.loadComparison(properties)),
        takeUntil(this.destroy$)
      )
      .subscribe((notes) => {
        this.comparisonNotes = notes;
        // National values only reach localStorage here, so the deviation bars
        // are drawn on this second pass rather than the first render.
        this.build();
      });

    // What the visitor may see is read once, at build time, so signing in
    // while this popup is open otherwise left every padlock - the overall
    // grade included - exactly as it was until the page was reloaded. The
    // numbers themselves are already in `properties` (the features endpoint
    // answers anonymous callers too), so re-running the build is the whole
    // fix. `skip(1)` drops the BehaviorSubject's current value: ngOnChanges
    // builds the first render.
    this.accessService.accessStatus
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.build());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTabId']?.firstChange) {
      this.activeTabId = this.renderedTabId = this.initialTabId;
    }

    if (changes['properties']) {
      this.indexProperties();
      this.properties$.next(this.properties);
    }

    this.build();
  }

  ngOnInit(): void {
    this.guideService.highlightFeature$
      .pipe(takeUntil(this.destroy$))
      .subscribe((feature) => (this.guidedFeature = feature));

    this.guideService.highlightPulse$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pulse) => (this.guidedPulse = pulse));
  }

  ngAfterViewInit(): void {
    this.bindScrollChaining();
  }

  ngOnDestroy(): void {
    if (this.swapTimer !== null) clearTimeout(this.swapTimer);
    this.detachScrollChaining?.();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public selectTab(id: string): void {
    if (id === this.activeTabId) return;

    // A tab picked from the strip opens at its beginning, and leans in from
    // the side it was picked from, so the click and the scroll below leave the
    // card in the same state.
    this.goToTab(id, this.tabIndex(id) > this.tabIndex(this.activeTabId));
  }

  public get activeTab(): RenderTab | null {
    return this.tabs.find((tab) => tab.id === this.renderedTabId) ?? null;
  }

  /** What the panel is drawing - trails the strip by the leaving animation. */
  public get isOverview(): boolean {
    return this.renderedTabId === OVERVIEW_TAB_ID;
  }

  /** What the strip highlights, which answers the gesture immediately. */
  public get isOverviewSelected(): boolean {
    return this.activeTabId === OVERVIEW_TAB_ID;
  }

  /**
   * The grade has been asked for and has not answered yet. A padlock is not a
   * wait, and neither is a grade that has arrived.
   */
  public get isScorePending(): boolean {
    return this.isScoreLoading && !this.heroLock && !this.heroGrade;
  }

  /** Same, for one of the three sub-indices under it. */
  public isSubIndexPending(sub: SubIndexRow): boolean {
    return this.isScoreLoading && !sub.locked && !sub.grade;
  }

  /** A locked row is the whole point of showing it, so the row is the trigger. */
  public handleLockedClick(row: MetricRow): void {
    if (!row.lock) return;
    this.unlockRequested.emit({ feature: row.feature, label: row.label });
  }

  /** Paints the map with this metric, exactly as the right menu's radio does. */
  public handleColorByClick(row: MetricRow): void {
    if (!row.colorable || row.onMap) return;
    this.colorByRequested.emit({ feature: row.feature, label: row.label });
  }

  /** Gated on the paid traffic layer, not the right menu's free chart. */
  public handleTrafficLockedClick(): void {
    this.unlockRequested.emit({
      feature: CUMULATIVE_TRAFFIC_INTENSITY,
      label: 'Traffic intensity',
    });
  }

  public handleGenerationsLockedClick(): void {
    this.unlockRequested.emit({ label: 'The generation mix' });
  }

  public handleHeroLockedClick(): void {
    this.unlockRequested.emit({ feature: HERO_SCORE, label: this.heroTitle });
  }

  /** Water cells carry the administrative area's data, which skews everything. */
  public get isMostlyWater(): boolean {
    const fraction = this.lookup.get(LAND_AREA_FRACTION);
    return (
      typeof fraction === 'number' &&
      fraction < 0.75 &&
      (this.activeLevel === H3_RES5_LEVEL_LAYER ||
        this.activeLevel === H3_RES7_LEVEL_LAYER)
    );
  }

  /**
   * Set when the map is coloured by a county-only layer at a finer level and
   * has nothing to show for it, so the empty pinned row says why instead of
   * leaving a bare `N/A`.
   *
   * Gated on the row actually being empty: a value and "published only at the
   * county level" side by side would contradict each other.
   */
  public get activeLayerLevelNote(): string | null {
    if (this.activeRow?.value !== 'N/A') return null;

    const isCountyOnly = COUNTY_ONLY_FEATURES.some(
      (feature) => feature.toUpperCase() === this.activeLayer?.toUpperCase()
    );

    return isCountyOnly && this.activeLevel !== COUNTY_LEVEL_LAYER
      ? COUNTY_ONLY_UNAVAILABLE_NOTE
      : null;
  }

  public get isTinyPopulation(): boolean {
    const population = this.lookup.get(POPULATION.toUpperCase());
    return typeof population === 'number' && population < 10;
  }

  /**
   * Bar geometry. The track is the full row and the centre tick is the
   * national value, so a fill of `n%` reaches `n/50` of the way to saturation
   * on whichever side of the country the cell sits.
   *
   * The 2% floor keeps a real-but-small gap visible: without it every metric
   * within a few percent of the country renders as an empty track, which reads
   * as missing data rather than as a close match.
   */
  public fillWidth(deviation: MetricDeviation): number {
    if (deviation.offset === 0) return 0;
    return Math.max(Math.abs(deviation.offset) * 50, 2);
  }

  public fillOffset(deviation: MetricDeviation): number {
    return deviation.offset >= 0 ? 50 : 50 - this.fillWidth(deviation);
  }

  public trackByFeature(_index: number, row: MetricRow): string {
    return row.feature;
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  /**
   * Scrolling past the end of a panel opens the next tab, and past the top of
   * one returns to the end of the previous: the card reads as a single column
   * with the tab strip as its index, rather than six lists behind six buttons.
   *
   * Bound outside Angular. A wheel fires at frame rate and a finger faster
   * still; only an actual tab switch is worth a change-detection pass over the
   * app.
   */
  private bindScrollChaining(): void {
    const panel = this.panelRef?.nativeElement;
    if (!panel || this.detachScrollChaining) return;

    const onWheel = (event: WheelEvent) => this.onPanelWheel(panel, event);
    const onTouchStart = (event: TouchEvent) =>
      this.onPanelTouchStart(panel, event);
    const onTouchMove = (event: TouchEvent) => this.onPanelTouchMove(event);
    const onTouchEnd = () => (this.touchStartY = null);

    this.zone.runOutsideAngular(() => {
      // Not passive: at the edge the gesture belongs to the tabs, and the
      // browser has to hear that before it plays its own end-of-scroll effect.
      panel.addEventListener('wheel', onWheel, { passive: false });
      // Passive: the native scroll is left alone, because a swipe is only ever
      // read as a tab change when the panel had nowhere to scroll anyway.
      panel.addEventListener('touchstart', onTouchStart, { passive: true });
      panel.addEventListener('touchmove', onTouchMove, { passive: true });
      panel.addEventListener('touchend', onTouchEnd, { passive: true });
      panel.addEventListener('touchcancel', onTouchEnd, { passive: true });
    });

    this.detachScrollChaining = () => {
      panel.removeEventListener('wheel', onWheel);
      panel.removeEventListener('touchstart', onTouchStart);
      panel.removeEventListener('touchmove', onTouchMove);
      panel.removeEventListener('touchend', onTouchEnd);
      panel.removeEventListener('touchcancel', onTouchEnd);
      this.detachScrollChaining = null;
    };
  }

  private onPanelWheel(panel: HTMLElement, event: WheelEvent): void {
    if (event.deltaY === 0) return;

    const forward = event.deltaY > 0;

    if (!this.isAtEdge(panel, forward)) {
      this.resetOverscroll();
      return;
    }

    const target = this.neighbourTab(forward);
    if (!target) {
      // First tab going up, last going down: the card ends here, and the
      // browser may have its overscroll back.
      this.resetOverscroll();
      return;
    }

    event.preventDefault();

    const now = performance.now();
    if (now - this.lastSwitchAt < TAB_SWITCH_COOLDOWN_MS) return;

    if (!this.edgeSince) this.edgeSince = now;

    if (now - this.edgeSince < EDGE_DWELL_MS) {
      // Still the flick that brought us here, not a request for the next tab.
      this.overscroll = 0;
      return;
    }

    this.overscroll += Math.abs(wheelPixels(event, panel));
    if (this.overscroll < TAB_SCROLL_THRESHOLD) return;

    this.zone.run(() =>
      this.goToTab(target, forward, forward ? 'start' : 'end')
    );
  }

  /**
   * Where the panel stood when the finger landed decides whether this gesture
   * may change tab at all - a swipe that starts mid-list scrolls the list, all
   * the way to its end, and never jumps.
   */
  private onPanelTouchStart(panel: HTMLElement, event: TouchEvent): void {
    if (event.touches.length !== 1) {
      this.touchStartY = null;
      return;
    }

    this.touchStartY = event.touches[0].clientY;
    this.touchFromEnd = this.isAtEdge(panel, true);
    this.touchFromStart = this.isAtEdge(panel, false);
  }

  private onPanelTouchMove(event: TouchEvent): void {
    if (this.touchStartY === null || event.touches.length !== 1) return;

    const travelled = event.touches[0].clientY - this.touchStartY;
    if (Math.abs(travelled) < TAB_SWIPE_THRESHOLD) return;

    const forward = travelled < 0;
    if (forward ? !this.touchFromEnd : !this.touchFromStart) return;

    // One switch per gesture, whatever the rest of the swipe does.
    this.touchStartY = null;

    const target = this.neighbourTab(forward);
    if (!target) return;
    if (performance.now() - this.lastSwitchAt < TAB_SWITCH_COOLDOWN_MS) return;

    this.zone.run(() =>
      this.goToTab(target, forward, forward ? 'start' : 'end')
    );
  }

  /**
   * @param land where the incoming panel starts: `end` keeps a backwards
   *   scroll continuous by handing back the bottom of the tab it came from.
   */
  private goToTab(
    id: string,
    forward: boolean,
    land: 'start' | 'end' = 'start'
  ): void {
    this.activeTabId = id;
    this.tabSelected.emit(id);
    this.ensureCardsLoaded();
    this.lastSwitchAt = performance.now();
    this.resetOverscroll();

    const panel = this.panelRef?.nativeElement;

    if (!panel || prefersReducedMotion()) {
      this.renderedTabId = id;
      if (panel) panel.scrollTop = land === 'end' ? panel.scrollHeight : 0;
      return;
    }

    if (this.swapTimer !== null) clearTimeout(this.swapTimer);

    setAnimation(panel, forward ? 'is-leaving-forward' : 'is-leaving-back');

    this.swapTimer = this.zone.runOutsideAngular(() =>
      window.setTimeout(
        () => this.zone.run(() => this.swapPanel(panel, forward, land)),
        PANEL_LEAVE_MS
      )
    );
  }

  /**
   * The outgoing list is gone by now (the leaving animation holds it at zero,
   * which is what `both` is for in the stylesheet), so this swaps the content
   * underneath it and plays the incoming one in.
   */
  private swapPanel(
    panel: HTMLElement,
    forward: boolean,
    land: 'start' | 'end'
  ): void {
    this.swapTimer = null;
    // Not the id the switch started with: `build` may have fallen back to the
    // overview in the meantime, if that tab turned out to have nothing in it.
    this.renderedTabId = this.activeTabId;

    // One frame later, because the panel is still showing the old tab's rows:
    // Angular's tick runs on the microtask queue, which drains before the
    // frame does.
    this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() => {
        panel.scrollTop = land === 'end' ? panel.scrollHeight : 0;
        setAnimation(
          panel,
          forward ? 'is-entering-forward' : 'is-entering-back'
        );
      })
    );
  }

  private isAtEdge(panel: HTMLElement, forward: boolean): boolean {
    return forward
      ? panel.scrollTop + panel.clientHeight >=
          panel.scrollHeight - EDGE_TOLERANCE
      : panel.scrollTop <= EDGE_TOLERANCE;
  }

  private resetOverscroll(): void {
    this.overscroll = 0;
    this.edgeSince = 0;
  }

  /** Overview first, then the subjects, in the order the strip draws them. */
  private get tabIds(): string[] {
    return [OVERVIEW_TAB_ID, ...this.tabs.map((tab) => tab.id)];
  }

  private tabIndex(id: string): number {
    return this.tabIds.indexOf(id);
  }

  private neighbourTab(forward: boolean): string | null {
    const ids = this.tabIds;

    return ids[ids.indexOf(this.activeTabId) + (forward ? 1 : -1)] ?? null;
  }

  private indexProperties(): void {
    this.lookup.clear();
    for (const key in this.properties ?? {}) {
      // Tile server properties arrive lowercase, backend ones uppercase, and
      // the same metric can come from either depending on the zoom level.
      this.lookup.set(key.toUpperCase(), this.properties[key]);
    }
  }

  /** Comparison notes keyed the way `buildRow` looks them up. */
  private loadComparison(properties: {
    [key: string]: any;
  }): Observable<{ [feature: string]: string }> {
    return this.comparisonService
      .getAndCalculateComparisonData(properties)
      .pipe(
        map((notes) => {
          const byFeature: { [feature: string]: string } = {};
          for (const key in notes ?? {}) {
            if (notes![key]) byFeature[key.toUpperCase()] = notes![key];
          }
          return byFeature;
        })
      );
  }

  private build(): void {
    const plan = this.accessService.getUserPlan();

    this.buildHero(plan);

    this.activeRow = this.activeLayer
      ? this.buildRow(this.activeLayer, plan, true)
      : null;

    this.tabs = POPUP_TABS.map((tab) => this.buildTab(tab, plan)).filter(
      (tab) => tab.sections.length > 0
    );

    // A tab can still come back empty once its requests answer - most likely
    // the remembered one, on a cell without that data.
    if (!this.tabIds.includes(this.activeTabId)) {
      this.activeTabId = this.renderedTabId = OVERVIEW_TAB_ID;
    }

    this.glance = GLANCE_TILES.map(({ feature, icon }) => {
      const row = this.buildRow(feature, plan, false);
      return row
        ? { ...row, icon, caption: GLANCE_LABELS[feature] ?? row.label }
        : null;
    }).filter((tile): tile is GlanceTile => tile !== null);

    this.standouts = this.pickStandouts();

    // Re-read on every build, so gaining access while open unlocks and loads.
    this.trafficLock = this.lockFor(CUMULATIVE_TRAFFIC_INTENSITY);
    // Not `lockFor`: the nearest indicator (age-sex chart) is paid, and this
    // breakdown is free with an account.
    this.generationsLock = this.accessService.isUnauthorized
      ? ACCOUNT_LOCK
      : null;
    this.ensureCardsLoaded();
  }

  /**
   * Section cards are fetched once, when their tab is first opened: most cells
   * are clicked for the score and closed again.
   */
  private ensureCardsLoaded(): void {
    if (this.trafficState === 'idle' && !this.trafficLock) {
      const geoId = this.geoIdIfSectionOpen(TRAFFIC_SECTION_ID);
      if (geoId) this.loadTraffic(geoId);
    }

    // Loaded for guests too: the free cohort is drawn from it.
    if (this.generationsState === 'idle') {
      const geoId = this.geoIdIfSectionOpen(CUSTOMERS_SECTION_ID);
      if (geoId) this.loadGenerations(geoId);
    }
  }

  private geoIdIfSectionOpen(sectionId: string): string | null {
    const isOpen = this.tabs.some(
      (tab) =>
        tab.id === this.activeTabId &&
        tab.sections.some((section) => section.id === sectionId)
    );

    return isOpen ? this.selectedCellService.getSelectedCellGeoId() : null;
  }

  private loadTraffic(geoId: string): void {
    this.trafficState = 'loading';

    forkJoin({
      intensity: this.http
        .getTrafficIntensityChartData(geoId)
        .pipe(catchError(() => of(null))),
      roads: this.http
        .getRoadDensityData(geoId)
        .pipe(catchError(() => of(null))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ intensity, roads }) => {
        const aadt = intensity?.object?.distribution;
        // Centreline length, not lane-weighted: AADT counts vehicles per road.
        const lengths = roads?.object?.roadLength;
        const traffic =
          aadt && lengths ? summariseTraffic(aadt, lengths) : null;

        if (traffic) {
          this.trafficOverall = formatTrafficCount(traffic.overall);
          this.trafficSplit = trafficSplitSentence(traffic);
          this.trafficState = 'ready';
          return;
        }

        // `null` is the catchError above; anything else is "no data here".
        this.trafficState =
          intensity === null || roads === null ? 'failed' : 'empty';
      });
  }

  private loadGenerations(geoId: string): void {
    this.generationsState = 'loading';

    this.http
      .getAgeSexData(geoId)
      .pipe(
        catchError(() => of(null)),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        if (data === null || data.error) {
          this.generationsState = 'failed';
          return;
        }

        // Every census year comes back; only the latest is read.
        const byYear = mapAgeSexDistribution(data.object ?? [])
          .byYears()
          .asArray();
        const latest = byYear[byYear.length - 1];

        // Scale counts to the population shown elsewhere in the popup.
        const population = this.lookup.get(POPULATION.toUpperCase());
        this.generations = latest
          ? generationBreakdown(
              latest[1],
              latest[0],
              typeof population === 'number' ? population : null
            )
          : [];

        this.generationsYear = latest?.[0] ?? 0;
        this.generationsState = this.generations.length ? 'ready' : 'empty';
      });
  }

  public isGenerationLocked(generation: GenerationSlice): boolean {
    return !!this.generationsLock && generation.id !== FREE_GENERATION_ID;
  }

  public formatGenerationPeople(people: number): string {
    return Math.round(people).toLocaleString('en-US');
  }

  public formatGenerationShare(share: number): string {
    return `${Math.round(share * 100)}%`;
  }

  /**
   * The metrics furthest from the national value, largest gap first.
   *
   * Ranked on the bar's own `offset` rather than the raw percentage, so a
   * ratio metric at 3x the country and a trend 2 points above it can be
   * compared on one scale. Whatever the map is already coloured by is left
   * out, being pinned directly above.
   *
   * Locked metrics are ranked alongside the visible ones and rendered
   * redacted, the same way they are inside the tabs. They used to be dropped
   * here, which quietly made the heading a lie for a guest: the four rows on
   * screen were the furthest *of the ones they were allowed to see*, and the
   * genuine outlier one padlock away was never mentioned.
   */
  private pickStandouts(): MetricRow[] {
    const seen = new Set<string>([this.activeLayer?.toUpperCase()]);
    const excluded = new Set(
      POPUP_TABS.flatMap((tab) =>
        tab.sections
          .filter((section) => section.excludeFromHighlights)
          .flatMap((section) => section.features.map((f) => f.toUpperCase()))
      )
    );
    const candidates: { row: MetricRow; gap: number }[] = [];

    for (const tab of this.tabs) {
      for (const section of tab.sections) {
        for (const row of section.rows) {
          const key = row.feature.toUpperCase();
          if (seen.has(key) || excluded.has(key)) continue;
          // A locked row carries no deviation of its own - drawing one would
          // hand over the number the padlock is hiding - so the gap it is
          // ranked on is measured here and goes no further than this sort.
          const deviation = row.deviation ?? this.deviationOf(row.feature);
          if (!deviation) continue;
          seen.add(key);
          candidates.push({ row, gap: Math.abs(deviation.offset) });
        }
      }
    }

    return candidates
      .sort((a, b) => b.gap - a.gap)
      .slice(0, STANDOUT_COUNT)
      .map(({ row }) => row);
  }

  /**
   * How far `feature` sits from the US, whether or not the visitor may see
   * it. The single definition of the gap: `buildRow` draws it, `pickStandouts`
   * ranks on it, and a locked row uses it without exposing it.
   */
  private deviationOf(feature: string): MetricDeviation | null {
    const raw = this.lookup.get(feature.toUpperCase());
    if (typeof raw !== 'number') return null;

    return nationalDeviation(
      feature,
      raw,
      hasNationalComparison(feature) ? nationalValueOf(feature) : null
    );
  }

  /**
   * Every metric the right menu can paint the map with, upper-cased.
   *
   * Read from the menu rather than restated here: it is the only place that
   * knows which metrics have a map layer behind them, and a row offering to
   * recolour the map with something that has none would simply blank it. The
   * menu's group data is built once at construction, so this is cached.
   */
  private get mapColorFeatures(): Set<string> {
    if (!this.mapColorCache) {
      this.mapColorCache = new Set(
        this.layersMenuService.groupsData
          .flatMap((group) => group.data.mapColor ?? [])
          .map((block) => block.featureConst.toUpperCase())
      );
    }

    return this.mapColorCache;
  }

  /**
   * Whether the map has a layer for `feature`.
   *
   * The HWW indices are the one family the menu registers unscoped
   * (`WEALTH_INDEX`) while the map is painted with the scoped name
   * (`WEALTH_INDEX_by_country`), which is also what this popup reads. Both
   * spellings mean the same layer, so the base name is what gets looked up.
   */
  private isColorable(feature: string): boolean {
    const key = feature.toUpperCase();
    return (
      this.mapColorFeatures.has(key) ||
      this.mapColorFeatures.has(key.split('_BY_')[0])
    );
  }

  /**
   * Opens the hint as a dialog, the way the right menu's own (?) does.
   *
   * The hover tooltip is the whole affordance on a laptop and none of it on a
   * phone, where a tap is not a hover and Material only reveals a tooltip on a
   * long press. The dialog is what makes the icon work with a finger.
   */
  public handleScoreHintClick(modalData: MapModalData): void {
    this.modalService.openModal(modalData);
  }

  /** Shared with the right menu's locked charts, see `lockCopyFor`. */
  private lockFor(feature: string): LockCopy | null {
    return lockCopyFor(feature, this.accessService);
  }

  private buildHero(plan: SubscriptionPlans | null): void {
    // "Neighborhood Score by Country" — the scope is spelled out in the
    // caption underneath, so it would only be said twice here.
    this.heroTitle = formatLayerName(HERO_SCORE, plan)
      .replace(/\s+by\s+Country$/i, '')
      .trim();

    this.heroLock = this.lockFor(HERO_SCORE);
    this.heroGrade = this.heroLock
      ? null
      : hwwGrade(HERO_SCORE, this.lookup.get(HERO_SCORE.toUpperCase()));

    this.subIndices = HERO_SUB_INDICES.map((feature, index) => {
      const locked = !!this.lockFor(feature);
      return {
        label: SUB_INDEX_LABELS[index],
        locked,
        grade: locked
          ? null
          : hwwGrade(feature, this.lookup.get(feature.toUpperCase())),
      };
    });
  }

  private buildTab(tab: PopupTab, plan: SubscriptionPlans | null): RenderTab {
    const sections = tab.sections
      .filter(
        (section) =>
          !section.levels || section.levels.includes(this.activeLevel)
      )
      .map((section) => ({
        id: section.id ?? null,
        label: section.label,
        note: section.note ?? null,
        rows: section.features
          .map((feature) => this.buildRow(feature, plan, false))
          .filter((row): row is MetricRow => row !== null),
        alwaysShown: !!section.alwaysShown,
      }))
      .filter((section) => section.rows.length > 0 || section.alwaysShown);

    const locks = sections.flatMap((section) =>
      section.rows
        .map((row) => row.lock)
        .filter((lock): lock is LockCopy => lock !== null)
    );

    return {
      id: tab.id,
      label: tab.label,
      icon: tab.icon,
      sections,
      lock: locks.find((lock) => !lock.free) ?? locks[0] ?? null,
    };
  }

  /**
   * @param keepEmpty the active layer keeps its row even with nothing in it,
   *   because "N/A here" is the answer to the question the visitor just asked
   *   by clicking the cell. Everywhere else an empty row is noise.
   */
  private buildRow(
    feature: string,
    plan: SubscriptionPlans | null,
    keepEmpty: boolean
  ): MetricRow | null {
    // The HWW indices come back named for their scope ("Income Index by
    // Country"). Every number in this card is read against the country, the
    // score block above says so once, and the row has no width to spare.
    const name = formatLayerName(feature, plan).replace(
      /\s+by\s+country$/i,
      ''
    );
    if (!name) return null;

    const lock = this.lockFor(feature);
    const raw = this.lookup.get(feature.toUpperCase());
    const { label, prefix, suffix } = splitUnit(name);

    const onMap =
      !!this.activeLayer &&
      feature.toUpperCase() === this.activeLayer.toUpperCase();

    if (lock) {
      return {
        feature,
        label,
        value: '',
        lock,
        deviation: null,
        reference: '',
        note: '',
        // The padlock owns the click on a locked row; painting the map with a
        // number the visitor cannot read would be the worst of both.
        colorable: false,
        onMap,
        grade: null,
        pending: false,
      };
    }

    const isEmpty = raw == null || raw === '';
    const formatted = isEmpty ? '' : formatNumbers(raw, 2, feature, true);
    const pending =
      formatted === '' && this.pendingFeatures.has(feature.toUpperCase());
    if (formatted === '' && !keepEmpty && !pending) return null;

    // An extensive quantity or one whose country figure is not the same
    // measure has no honest reference, so neither the caption nor the bar is
    // drawn — see NO_COMPARISON_EXACT in national-comparison.ts.
    const national = hasNationalComparison(feature)
      ? nationalValueOf(feature)
      : null;
    // The reference goes through the *same* formatter as the cell's own value
    // above. Anything else and the two numbers in the row are not comparable —
    // an earlier pre-scaling step here turned a national obesity rate of 0.32
    // into "3200%", because formatNumbers already multiplies these fractions
    // by 100 on its own.
    const reference =
      national == null
        ? ''
        : `US avg ${prefix}${formatNumbers(national, 2, feature, true)}${suffix}`;

    return {
      feature,
      label,
      value: pending
        ? ''
        : formatted === ''
          ? 'N/A'
          : `${prefix}${formatted}${suffix}`,
      lock: null,
      deviation: this.deviationOf(feature),
      reference,
      note: this.comparisonNotes[feature.toUpperCase()] ?? reference,
      colorable: this.isColorable(feature),
      onMap,
      // Same call the score block makes, so the letter and the fill in a tab
      // cannot disagree with the ones on the overview.
      grade: hwwGrade(feature, raw),
      pending,
    };
  }

  protected readonly TRAFFIC_SECTION_ID = TRAFFIC_SECTION_ID;
  protected readonly TRAFFIC_TEASER_SECTION_ID = TRAFFIC_TEASER_SECTION_ID;
  protected readonly CUSTOMERS_SECTION_ID = CUSTOMERS_SECTION_ID;
}

/** The traffic card's second line: how highways and local streets split. */
function trafficSplitSentence(traffic: TrafficSummary): string {
  const { local, highway } = traffic;

  if (highway === null) {
    return 'No motorway or trunk road crosses this area, so all of it is local traffic.';
  }

  if (local === null) {
    return `Every road counted here is a highway, carrying about ${formatTrafficCount(highway)} a day.`;
  }

  return `Highways carry about ${formatTrafficCount(highway)} a day; the streets off them average ${formatTrafficCount(local)}.`;
}

/**
 * Wheel delta in CSS pixels. Firefox reports lines and a page-scrolling mouse
 * reports pages, and a threshold measured in pixels has to compare like with
 * like.
 */
function wheelPixels(event: WheelEvent, panel: HTMLElement): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * WHEEL_LINE_HEIGHT;
  }

  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * panel.clientHeight;
  }

  return event.deltaY;
}

/**
 * Swaps the panel's animation class. Re-adding a class the element already
 * carries would not replay the animation, and the reflow between is what makes
 * a second switch in the same direction start over rather than sit still.
 */
function setAnimation(panel: HTMLElement, className: string): void {
  panel.classList.remove(
    'is-leaving-forward',
    'is-leaving-back',
    'is-entering-forward',
    'is-entering-back'
  );
  void panel.offsetWidth;
  panel.classList.add(className);
}

/** The panel swap is instant for a reader who has asked for less movement. */
function prefersReducedMotion(): boolean {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}
